/* istanbul ignore file */

// WebPack generation of HTML files under `/demo`
// import './demo/index.html';
import React from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { HistoryServiceV3 } from '@feature-hub/history-service';
import type { FeatureAppDefinition, FeatureServices, Logger } from '@feature-hub/core';
import type { LayerManagerV27 } from '@volkswagen-onehub/layer-manager';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import { SpawnContext } from '@oneaudi/falcon-tools';
import OneLayerCollection from './components/one-layer-collection';
import { LayerContentHTML } from './utils';
import { AppContextProvider } from './context/app';
import { clearOneLayerQuery } from './components/util';

export const FEATURE_APP_NAME = 'oneaudi-feature-app-one-layer';

export interface Dependencies extends FeatureServices {
  readonly 's2:logger': Logger;
  readonly 'layer-manager': LayerManagerV27;
  readonly 'gfa:service-config-provider': GfaServiceConfigProviderV1;
  readonly 'audi-tracking-service': TrackingServiceV2;
  readonly 's2:history': HistoryServiceV3;
  readonly 'audi-content-service': ContentServiceV1;
}

interface Config {
  zIndexAlert: number;
  zIndex?: number;
  fefaFeatureAppUrl?: string;
  oneFootnoteUrl?: string;
  oneFootnoteUrls?: string[];
}

type StaticFalconConfig = {
  __type?: 'aem-headless';
  fields: {
    zIndexAlert?: number;
    zIndex?: number;
    fefaFeatureAppUrl?: string;
    oneFootnoteUrls?: string[];
  };
};

type FeatureAppDefinitionType = FeatureAppDefinition<ReactFeatureApp, Dependencies, Config>;

function normalizeConfig(config?: Config | StaticFalconConfig): Config | undefined {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (config.__type === 'aem-headless') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return config?.config?.fields as Config;
  }
  return config as Config;
}

const featureAppDefinition: FeatureAppDefinitionType = {
  create: ({ featureServices: services, config: rawConfig, featureAppId }) => {
    const config = normalizeConfig(rawConfig);
    const zIndexAlert = config?.zIndexAlert || 100;
    const zIndex = config?.zIndex;
    const fefaFeatureAppUrl = config?.fefaFeatureAppUrl;
    let oneFootnoteUrls: string[] | undefined = [];

    if (config?.oneFootnoteUrl && config?.oneFootnoteUrl !== '') {
      oneFootnoteUrls.push(config?.oneFootnoteUrl);
    } else {
      oneFootnoteUrls = config?.oneFootnoteUrls;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isOneCMS = rawConfig?.__type === 'aem-headless';
    // const fefaFeatureAppUrl = config?.fefaFeatureAppUrl;
    // const oneFootnoteJsonUrl = config?.oneFootnoteJsonUrl;

    const layerManager = services['layer-manager'];
    const historyService = services['s2:history'];
    const trackingService = services['audi-tracking-service'];
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }
    const serviceConfigProvider = services['gfa:service-config-provider'];
    const contentApiConfig = isOneCMS ? serviceConfigProvider?.audi['falcon-content-api'] : null;
    if (typeof window !== 'undefined') {
      const handleHashChange = () => {
        let url: string | null;

        const urlHash = window.location.hash;
        const currentUrl = new URL(window.location.href);
        const matches = urlHash.match(/(?:one-layer|oneLayer)=([^&]*)/);
        const { protocol } = currentUrl;
        const hasDeepLink = urlHash.includes('one-layer') || urlHash.includes('oneLayer');
        url = urlHash.replace('#oneLayer=', '').replace('#one-layer=', '');
        url = matches && decodeURIComponent(matches[1]);
        const isProtocol = url?.startsWith('audi') ? `${protocol}/` : '';
        const htmlUrl = `${isProtocol}/${url}`;
        const cleanedUrl = htmlUrl.replace(/^\/\//, '/');
        const urlParams = window.location.search;
        const fefaParam = urlParams?.includes('withFefa');

        const LayerContentHTMLComponent = fefaParam ? (
          <LayerContentHTML fromLayer enableDeepLinking url={cleanedUrl} withFefa />
        ) : (
          <LayerContentHTML fromLayer enableDeepLinking url={cleanedUrl} />
        );

        if (hasDeepLink && cleanedUrl && layerManager) {
          // Protocol and Host need to be filled in because url is is written without them
          // like href='/de/brand/de/tools/navigation/layer/impressum.html'

          layerManager.openFocusLayer(
            () => LayerContentHTMLComponent,
            {},
            {
              size: FocusLayerSizeV2.A,
              userCloseable: true,
              onClose: () => {
                const layers = layerManager.getLayers();
                clearOneLayerQuery(layers.length, historyService);
              },
            },
          );
        }
      };
      window.addEventListener('hashchange', handleHashChange);
      handleHashChange();
    }

    return {
      // disable eslint rule because this is a (Feature Hub) React Feature App,
      // that doesn't define a `displayName` attribute

      render: (): React.ReactNode => (
        <ThemeProvider theme={{ name: 'light' }}>
          <AppContextProvider
            historyService={historyService}
            // @ts-expect-error content api cofing may be null
            contentApiConfig={contentApiConfig}
            isHeadless={isOneCMS}
            fefaFeatureAppUrl={fefaFeatureAppUrl}
            oneFootnoteUrls={oneFootnoteUrls}
          >
            <SpawnContext>
              <OneLayerCollection
                {...{ featureAppId, zIndex, zIndexAlert, historyService }}
                layerManager={layerManager}
              />
            </SpawnContext>
          </AppContextProvider>
        </ThemeProvider>
      ),
    };
  },

  dependencies: {
    featureServices: {
      'layer-manager': '^2',
      's2:logger': '^1.0.0',
      's2:history': '^3.0.0',
      'gfa:service-config-provider': '^1.0.0',
      'audi-content-service': '^1.0.0',
    },
    externals: {
      react: '^18.2.0',
      '@feature-hub/react': '^3',
      'styled-components': '^5.1.1',
      '@oneaudi/unified-web-common': '*',
    },
  },

  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
    },
  },
};

export default featureAppDefinition;
